import * as THREE from 'three';
import { cloth, emis, std } from '../materials.ts';
import { M, V, glowSprite, limb } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';
import { taper, yjoint } from './weaver.ts';

/*
 * La Sima (docs/SIMA.md «Criaturas nuevas»): one new nightmare per new stratum, each carrying its stratum's colour
 * so a floor reads at a glance. Cisterns, the leech (teal-green); buried wood, the root scarecrow (moss); grotto,
 * the walking puffball (cyan); frost veins, the living rime (pale ice); geode, the crystal echo (violet); archive,
 * the scribe (cold grey, ink and sealing-wax red); dead forge, the molten (red-hot); ribs, the bone gnawers (grey
 * bone and dark red).
 *
 * All opaque shared materials but the ice, a handful per model, glow sprites for light (never point lights), and
 * every random detail from the model's own seeded rng. The swarms (leech, boneworm) stay under ~600 triangles.
 * Each faces +Z, origin at the base centre; the bones and their conventions are in anim/creatures/sima.ts.
 */

const UP = V(0, 1, 0);

/** A mesh from `geo` placed at `p`, scaled, its +Y turned to `dir`. */
const along = (parent: THREE.Object3D, geo: THREE.BufferGeometry, mat: THREE.Material, p: THREE.Vector3, dir: THREE.Vector3): THREE.Mesh => {
  const m = M(geo, mat, parent);
  m.position.copy(p);
  m.quaternion.setFromUnitVectors(UP, dir.clone().normalize());
  return m;
};

/** A round blob (a stretched low sphere). */
const blob = (parent: THREE.Object3D, mat: THREE.Material, x: number, y: number, z: number, sx: number, sy: number, sz: number, w = 7, h = 5): THREE.Mesh => {
  const m = M(new THREE.SphereGeometry(1, w, h), mat, parent);
  m.position.set(x, y, z);
  m.scale.set(sx, sy, sz);
  return m;
};

/* ======================================================================================================== */
/* Sanguijuela (Cisternas)                                                                                   */
/* ======================================================================================================== */

/**
 * Sanguijuela: a glossy black leech as long as a forearm, out of the black water. Fat at the back where the
 * rear sucker grips, narrow at the front, where the mouth opens into a pale ring of little teeth; two rows of
 * teal-green spots run down its back, the cisterns' light wet on its hide.
 *
 * Bones: `body` (the middle), `fore` → `head` toward +Z, `hind` → `tail` toward −Z. −X on `fore`/`head` lifts the
 * front; +X on `hind`/`tail` lifts the back; a Y turn swings a segment sideways.
 */
export const leech = (o: BaseOpts = {}): Model => {
  const c = begin(o, 0.36, 901);
  const { g } = c;
  const hide = std('#070b0c', { roughness: 0.1, metalness: 0.45, bumpScale: 0.004 });
  const belly = std('#16302c', { roughness: 0.35, metalness: 0.2, bumpScale: 0.01 });
  const spot = emis('#9ff2df', '#5fd0b8', 0.85);
  const lip = std('#7f9c97', { roughness: 0.4 });

  const Y = 0.1;
  const bodyJ = joint(g, V(0, Y, 0), 'body');
  const foreJ = joint(bodyJ.abs, V(0, Y, 0.12), 'fore');
  const headJ = joint(foreJ.abs, V(0, Y, 0.26), 'head');
  const hindJ = joint(bodyJ.abs, V(0, Y, -0.12), 'hind');
  const tailJ = joint(hindJ.abs, V(0, Y, -0.26), 'tail');

  // Segments from the rear sucker to the mouth: [bone, z, half width, half height].
  const SEG: [THREE.Object3D, number, number, number][] = [
    [tailJ.abs, -0.38, 0.12, 0.07],
    [tailJ.abs, -0.29, 0.14, 0.1],
    [hindJ.abs, -0.17, 0.15, 0.11],
    [bodyJ.abs, -0.04, 0.145, 0.105],
    [foreJ.abs, 0.09, 0.125, 0.095],
    [foreJ.abs, 0.2, 0.1, 0.08],
    [headJ.abs, 0.3, 0.075, 0.062],
  ];
  for (const [i, [b, z, w, h]] of SEG.entries()) {
    blob(b, hide, 0, Y - h * 0.1, z, w, h * 0.85, 0.13, 8, 4);
    // The pale wet belly flat on the ground.
    if (i > 0 && i < SEG.length - 1) blob(b, belly, 0, Y - h * 0.6, z, w * 0.85, h * 0.4, 0.11, 5, 3);
    // Two rows of spots down the back.
    if (i > 0 && i < SEG.length - 1) for (const k of [-1, 1]) blob(b, spot, k * w * 0.4, Y + h * 0.62, z, 0.016, 0.01, 0.04, 4, 2);
  }
  // The rear sucker: a flattened disc gripping the floor.
  const rear = M(new THREE.CylinderGeometry(0.1, 0.11, 0.03, 7), belly, tailJ.abs);
  rear.position.set(0, 0.02, -0.4);
  // The mouth: a pale ring opening forward, three little teeth in it.
  const mouth = along(headJ.abs, new THREE.CylinderGeometry(0.06, 0.045, 0.05, 8, 1, true), lip, V(0, Y, 0.37), V(0, 0.1, 1));
  mouth.scale.set(1, 1, 0.8);
  const throat = blob(headJ.abs, std('#2a0e12', { roughness: 0.6 }), 0, Y, 0.37, 0.04, 0.034, 0.02, 6, 4);
  throat.name = 'throat';
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + 0.5;
    along(headJ.abs, new THREE.ConeGeometry(0.009, 0.03, 4), lip, V(Math.cos(a) * 0.035, Y + Math.sin(a) * 0.03, 0.385), V(-Math.cos(a), -Math.sin(a), 0.6));
  }
  // The wet light on its back, faint (reads it against the dark floor).
  glowSprite(bodyJ.abs, '#5fd0b8', 0.55, V(0, Y + 0.1, -0.04), 0.18);

  return done(c, 0.24, undefined, undefined, bonesOf(c, { body: bodyJ.p, fore: foreJ.p, head: headJ.p, hind: hindJ.p, tail: tailJ.p }));
};

/* ======================================================================================================== */
/* Roedor de hueso (Costillas)                                                                               */
/* ======================================================================================================== */

/**
 * Roedor de hueso: a worm of grey bone the length of a man's arm, come up out of the floor where the ribs fell.
 * Its body is a string of vertebrae with spurs raking back, its rear dragging and the front reared like a snake's;
 * the head is a round mouth, a ring of teeth around a dark red throat.
 *
 * Bones: `body` (the middle vertebra), `tail` (the dragging rear, −Z), `neck` and `head` (the reared front, +Z).
 * −X on `neck`/`head` lifts the front higher; a Y turn swings it sideways.
 */
export const boneworm = (o: BaseOpts = {}): Model => {
  const c = begin(o, 0.36, 951);
  const { g, rng } = c;
  const bone = std('#7f878e', { roughness: 0.75, bumpScale: 0.02 });
  const sinew = std('#3b2529', { roughness: 0.55 });
  const tooth = std('#aab1b7', { roughness: 0.45 });
  const maw = emis('#4a0f16', '#d0464f', 0.9);

  // The spine's path: flat at the rear, rising into an S at the front.
  const path = new THREE.CatmullRomCurve3([V(0, 0.05, -0.42), V(0, 0.06, -0.22), V(0, 0.09, -0.04), V(0, 0.2, 0.1), V(0, 0.36, 0.16), V(0, 0.44, 0.27)]);
  const N = 9;
  const at = (i: number): THREE.Vector3 => path.getPointAt(i / N);
  const bodyJ = joint(g, at(3), 'body');
  const tailJ = joint(bodyJ.abs, at(2.5), 'tail');
  const neckJ = joint(bodyJ.abs, at(4.5), 'neck');
  const headJ = joint(neckJ.abs, at(7), 'head');
  const owner = (i: number): THREE.Object3D => (i < 3 ? tailJ.abs : i < 5 ? bodyJ.abs : i < 7 ? neckJ.abs : headJ.abs);

  const vert = new THREE.CylinderGeometry(1, 0.86, 1, 6);
  const gap = new THREE.CylinderGeometry(1, 1, 1, 5, 1, true);
  const spur = new THREE.ConeGeometry(0.014, 0.08, 4);
  for (let i = 0; i < N - 1; i++) {
    const a = at(i), b = at(i + 1);
    const mid = a.clone().lerp(b, 0.5);
    const dir = b.clone().sub(a);
    const r = 0.035 + 0.025 * Math.sin(((i + 1) / N) * Math.PI) + (i > 5 ? 0.008 : 0);
    // The vertebra (a squat drum) with a dark sinew gap behind it.
    const v = along(owner(i), vert, bone, mid, dir);
    v.scale.set(r, dir.length() * 0.78, r);
    const s = along(owner(i), gap, sinew, a.clone().lerp(b, 0.05), dir);
    s.scale.set(r * 0.78, dir.length() * 0.3, r * 0.78);
    // Spurs raking back and out, a pair to each vertebra (and one up the spine).
    for (const k of [-1, 1]) along(owner(i), spur, bone, mid.clone().add(V(k * r * 0.9, r * 0.3, 0)), V(k * 0.9, 0.5, -0.7 - rng() * 0.2).add(dir.clone().normalize().multiplyScalar(-0.3)));
    if (i % 2 === 0) along(owner(i), spur, bone, mid.clone().add(V(0, r * 0.9, 0)), V(0, 1, -0.6));
  }
  // The head: a wider ring of bone opening forward and a little down, teeth all round it, the throat red inside.
  const hp = at(N), hd = V(0, -0.25, 1);
  along(headJ.abs, new THREE.CylinderGeometry(0.075, 0.06, 0.08, 8, 1, true), bone, hp, hd);
  const throat = along(headJ.abs, new THREE.CircleGeometry(0.058, 8), maw, hp.clone().addScaledVector(hd.clone().normalize(), -0.015), hd);
  throat.rotateX(-Math.PI / 2);
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const side = V(Math.cos(a), Math.sin(a), 0);
    const p = hp.clone().add(V(side.x * 0.06, side.y * 0.058, 0)).addScaledVector(hd.clone().normalize(), 0.038);
    along(headJ.abs, new THREE.ConeGeometry(0.012, 0.05, 4), tooth, p, V(-side.x, -side.y, 0.7));
  }
  glowSprite(headJ.abs, '#d0464f', 0.22, hp.clone().addScaledVector(hd.clone().normalize(), 0.03), 0.55);

  return done(c, 0.5, undefined, undefined, bonesOf(c, { body: bodyJ.p, tail: tailJ.p, neck: neckJ.p, head: headJ.p }));
};

/* ======================================================================================================== */
/* Espantajo de raíces (Bosque enterrado)                                                                    */
/* ======================================================================================================== */

/**
 * Espantajo de raíces: a scarecrow the buried wood grew round an old pole. Thick roots braided into legs and a
 * trunk, black earth packed in its chest, moss on its shoulders and hanging in beards; the pole still runs across
 * its shoulders under the roots. A knot of root for a head with two holes that burn moss-green. The right arm ends
 * in a burl as big as a head (its club); the left trails long root fingers that drag.
 *
 * Bones: body (hips), chest, head, arms in two (arm, fore), legs in two (leg, shin). Conventions as human.ts.
 */
export const rootwight = (o: BaseOpts = {}): Model => {
  const c = begin(o, 0.62, 971);
  const { g, rng } = c;
  const k4 = kit(rng);
  const bark = std('#33261c', { roughness: 0.92, bumpScale: 0.05 });
  const barkD = std('#1d1611', { roughness: 0.95, flatShading: true, bumpScale: 0.06 });
  const earth = std('#120f0c', { roughness: 1, flatShading: true, bumpScale: 0.08 });
  const moss = emis('#38592a', '#7fc25a', 0.06, { roughness: 1, bumpScale: 0.08 });
  const mossHang = std('#30522a', { roughness: 1, side: THREE.DoubleSide, bumpScale: 0.05 });
  const eye = emis('#e2ffc0', '#8fe05a', 2.0);
  const R = (): number => rng() - 0.5;

  /** A braid of `n` roots from `a` to `b`, twisting round each other. */
  const braid = (parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, r: number, n: number, rad: number, mat: THREE.Material = bark): void => {
    const d = b.clone().sub(a);
    const side = V(-d.z, 0, d.x).normalize();
    if (side.lengthSq() < 0.01) side.set(1, 0, 0);
    const fw = d.clone().cross(side).normalize();
    for (let i = 0; i < n; i++) {
      const ph = (i / n) * Math.PI * 2;
      const pts: THREE.Vector3[] = [];
      for (let j = 0; j <= 4; j++) {
        const k = j / 4, tw = ph + k * 2.4;
        pts.push(a.clone().addScaledVector(d, k).addScaledVector(side, Math.cos(tw) * r).addScaledVector(fw, Math.sin(tw) * r));
      }
      M(taper(pts, rad, rad * 0.75, 6, 5, 1, 0.5), mat, parent);
    }
  };

  /* legs: braided root bundles, roots splaying out of the feet into the floor */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = joint(g, V(k * 0.2, 0.92, 0), `leg${s}`);
    braid(legB.abs, V(k * 0.2, 0.98, 0), V(k * 0.25, 0.5, 0.04), 0.065, 3, 0.07);
    const shinB = joint(legB.abs, V(k * 0.25, 0.5, 0.04), `shin${s}`);
    braid(shinB.abs, V(k * 0.25, 0.52, 0.04), V(k * 0.27, 0.06, 0.02), 0.06, 3, 0.065);
    k4.lump(shinB.abs, barkD, k * 0.26, 0.52, 0.06, 0.1, 0.09, 0.1, 0.5, 0);
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + 0.4;
      const toe = V(k * 0.27 + Math.sin(a) * 0.22, 0.0, 0.03 + Math.cos(a) * 0.2);
      M(taper([V(k * 0.27, 0.14, 0.03), V(k * 0.27 + Math.sin(a) * 0.1, 0.05, 0.03 + Math.cos(a) * 0.09), toe], 0.035, 0.008, 4, 4, 1, 1), bark, shinB.abs);
    }
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  /* hips and trunk: a braid of roots, black earth packed between */
  const bodyB = joint(g, V(0, 0.95, 0), 'body');
  k4.lump(bodyB.abs, earth, 0, 1.0, 0, 0.27, 0.16, 0.2, 0.4, 0);
  braid(bodyB.abs, V(0, 0.9, 0), V(0, 1.3, 0.02), 0.13, 4, 0.06);
  const chestB = joint(bodyB.abs, V(0, 1.25, 0), 'chest');
  const ch = chestB.abs;
  braid(ch, V(0, 1.2, 0.0), V(0, 1.78, 0.08), 0.2, 5, 0.085);
  k4.lump(ch, earth, 0, 1.52, 0.04, 0.3, 0.3, 0.24, 0.4, 1);
  k4.lump(ch, earth, 0.05, 1.36, 0.12, 0.16, 0.13, 0.12, 0.5, 0);
  // The heart of it: a hollow in the earth with a faint moss-light.
  glowSprite(ch, '#7fc25a', 0.4, V(0, 1.5, 0.26), 0.22);
  // The old pole across the shoulders, roots wound over it.
  const pole = std('#4a4038', { roughness: 0.9, bumpScale: 0.04 });
  limb(ch, V(-0.72, 1.74, -0.04), V(0.72, 1.76, -0.06), 0.035, 0.035, pole, 6);
  for (const k of [-1, 1]) for (let i = 0; i < 2; i++) {
    const x = k * (0.3 + i * 0.22);
    M(new THREE.TorusGeometry(0.05, 0.02, 4, 7), bark, ch).position.set(x, 1.75, -0.05);
  }
  // Moss on the shoulders and hanging in beards off the chest and the pole.
  for (const k of [-1, 1]) k4.lump(ch, moss, k * 0.28, 1.8, 0.02, 0.2, 0.08, 0.18, 0.4, 0);
  k4.lump(ch, moss, 0, 1.68, 0.18, 0.15, 0.07, 0.07, 0.3, 0);
  k4.panel(ch, V(0.02, 1.7, 0.2), 0.3, 0.42, 0.05, 1, mossHang, 0.5);
  for (const k of [-1, 1]) k4.panel(ch, V(k * 0.55, 1.73, -0.03), 0.16, 0.36, 0.03, 1, mossHang, 0.6);

  /* head: a knot of root, two burning holes, twigs raking back like a crown of straw */
  const headB = joint(ch, V(0, 1.84, 0.08), 'head');
  const hd = headB.abs;
  k4.lump(hd, bark, 0, 2.0, 0.08, 0.17, 0.2, 0.16, 0.4, 1, 0.25);
  k4.lump(hd, barkD, 0, 2.08, 0.14, 0.15, 0.06, 0.1, 0.3, 0);
  for (const k of [-1, 1]) {
    blob(hd, earth, k * 0.065, 2.0, 0.215, 0.04, 0.045, 0.02, 6, 4);
    blob(hd, eye, k * 0.065, 2.0, 0.225, 0.017, 0.02, 0.012, 5, 3);
    glowSprite(hd, '#9fe86a', 0.2, V(k * 0.065, 2.0, 0.24), 0.9);
  }
  // A torn mouth, black.
  const mouth = blob(hd, earth, 0, 1.9, 0.2, 0.07, 0.022, 0.03, 6, 3);
  mouth.rotation.z = 0.15;
  for (let i = 0; i < 6; i++) {
    const a = -1.2 + i * 0.48;
    M(taper([V(Math.sin(a) * 0.1, 2.12, 0.04), V(Math.sin(a) * 0.2 + R() * 0.05, 2.28, -0.08), V(Math.sin(a) * 0.28 + R() * 0.06, 2.34 + R() * 0.06, -0.22)], 0.018, 0.005, 4, 4, 1, 1), barkD, hd);
  }

  /* arms: the right a heavy root ending in a burl (its club), the left long, trailing root fingers */
  const arms: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const armB = joint(ch, V(k * 0.32, 1.72, 0.02), `arm${s}`);
    const elbow = V(k * 0.5, 1.3, 0.06);
    braid(armB.abs, V(k * 0.3, 1.74, 0.02), elbow, 0.04, k > 0 ? 3 : 2, k > 0 ? 0.065 : 0.05);
    k4.lump(armB.abs, barkD, k * 0.34, 1.72, 0.02, 0.12, 0.11, 0.12, 0.5, 0);
    const foreB = joint(armB.abs, elbow, `fore${s}`);
    const fo = foreB.abs;
    if (k > 0) {
      const wrist = V(0.52, 0.86, 0.14);
      braid(fo, elbow, wrist, 0.045, 3, 0.065);
      // The burl: a knotted club of root and packed earth, a stone grown into it.
      k4.lump(fo, bark, 0.53, 0.72, 0.16, 0.2, 0.22, 0.19, 0.6, 1, 0.3);
      k4.lump(fo, barkD, 0.55, 0.6, 0.2, 0.15, 0.12, 0.15, 0.6, 0, 0.3);
      k4.lump(fo, std('#4c4d50', { roughness: 0.9, flatShading: true }), 0.62, 0.72, 0.26, 0.07, 0.06, 0.06, 0.5, 0);
      k4.lump(fo, moss, 0.47, 0.84, 0.1, 0.11, 0.05, 0.1, 0.4, 0);
      for (let i = 0; i < 3; i++) {
        const a = i * 2.1;
        M(taper([V(0.53, 0.66, 0.16), V(0.53 + Math.cos(a) * 0.2, 0.62, 0.16 + Math.sin(a) * 0.2), V(0.53 + Math.cos(a) * 0.26, 0.56, 0.16 + Math.sin(a) * 0.26)], 0.022, 0.006, 3, 4, 1, 1), bark, fo);
      }
    } else {
      const wrist = V(-0.56, 0.9, 0.16);
      braid(fo, elbow, wrist, 0.025, 2, 0.04);
      for (let i = 0; i < 4; i++) {
        const sp = (i - 1.5) * 0.06;
        M(taper([wrist, V(-0.58 + sp, 0.68, 0.2 + sp * 0.5), V(-0.6 + sp * 1.4, 0.44, 0.22 + sp), V(-0.6 + sp * 1.8, 0.28, 0.2 + sp)], 0.024, 0.006, 6, 4, 1, 1), bark, fo);
      }
      k4.panel(fo, V(-0.52, 1.24, 0.06), 0.12, 0.3, 0.02, 1, mossHang, 0.6);
    }
    arms[`arm${s}`] = armB.p;
    arms[`fore${s}`] = foreB.p;
  }

  return done(c, 2.3, undefined, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p,
    armL: arms.armL!, foreL: arms.foreL!, armR: arms.armR!, foreR: arms.foreR!,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!,
  }));
};

/* ======================================================================================================== */
/* Bejín errante (Gruta de setas)                                                                            */
/* ======================================================================================================== */

/**
 * Bejín errante: a puffball as big as a barrel walking on two stubby root-legs. Its skin is pale cool grey and
 * scaly with warts; the top has split open in a star and the spores inside glow a pale cyan, smoking out of it.
 * No face: it walks at you all the same.
 *
 * Bones: `body` (the whole ball, pivot at its foot), `cap` (the top with the split, which the clips swell),
 * `legL`, `legR`. +X on the body leans it forward.
 */
export const puffball = (o: BaseOpts = {}): Model => {
  const c = begin(o, 0.5, 981);
  const { g, rng } = c;
  const k4 = kit(rng);
  const skin = std('#8ea3af', { roughness: 0.92, bumpScale: 0.06 });
  const wart = std('#5d6f7a', { roughness: 0.95, flatShading: true, bumpScale: 0.04 });
  const under = std('#2f3c45', { roughness: 0.95, bumpScale: 0.05 });
  const spore = emis('#d6f8ff', '#4fd6ff', 1.6);

  const bodyJ = joint(g, V(0, 0.24, 0), 'body');
  const bd = bodyJ.abs;
  const C = 0.62;
  // The ball, wider than tall, sitting low on its base.
  const ball = M(new THREE.SphereGeometry(0.4, 14, 10), skin, bd);
  ball.position.set(0, C, 0);
  ball.scale.set(1, 0.86, 1);
  // The base narrows to a wrinkled foot, darker.
  const foot = M(new THREE.CylinderGeometry(0.26, 0.2, 0.26, 10), under, bd);
  foot.position.set(0, 0.36, 0);
  // Warts and scales over the upper half.
  for (let i = 0; i < 18; i++) {
    const a = rng() * Math.PI * 2, el = 0.15 + rng() * 1.0;
    const p = V(Math.cos(a) * Math.cos(el) * 0.4, C + Math.sin(el) * 0.344, Math.sin(a) * Math.cos(el) * 0.4);
    k4.lump(bd, wart, p.x, p.y, p.z, 0.03 + rng() * 0.02, 0.018, 0.03 + rng() * 0.02, 0.5, 0);
  }
  // Pale cyan spots glowing through the skin low on its sides.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2 + rng() * 0.5, el = -0.15 + rng() * 0.45;
    blob(bd, spore, Math.cos(a) * Math.cos(el) * 0.398, C + Math.sin(el) * 0.344, Math.sin(a) * Math.cos(el) * 0.398, 0.022, 0.022, 0.022, 4, 3);
  }

  // The top: torn open in a star, the spores glowing in the tear.
  const capJ = joint(bd, V(0, C + 0.3, 0), 'cap');
  const cp = capJ.abs;
  const top = V(0, C + 0.335, 0);
  blob(cp, spore, 0, C + 0.31, 0, 0.11, 0.05, 0.11, 8, 4);
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.3;
    // The lips of the tear: curled flaps of skin.
    const flap = M(new THREE.SphereGeometry(1, 5, 3, 0, Math.PI * 2, 0, Math.PI / 2), skin, cp);
    flap.position.set(Math.cos(a) * 0.12, C + 0.32, Math.sin(a) * 0.12);
    flap.scale.set(0.07, 0.05, 0.1);
    flap.rotation.set(0, -a + Math.PI / 2, 0);
    flap.rotateX(-0.7);
    // The glowing seams running down from the tear.
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 4; j++) {
      const el = 1.25 - j * 0.28;
      const aa = a + 0.2 + j * 0.06;
      pts.push(V(Math.cos(aa) * Math.cos(el) * 0.405, C + Math.sin(el) * 0.35, Math.sin(aa) * Math.cos(el) * 0.405));
    }
    k4.crack(cp, pts, 0.012, spore, 0.02);
  }
  glowSprite(cp, '#4fd6ff', 0.75, top.clone().add(V(0, 0.04, 0)), 0.6);
  // Spores smoking off it.
  for (let i = 0; i < 4; i++) glowSprite(cp, '#9feaff', 0.12 + rng() * 0.08, V((rng() - 0.5) * 0.3, C + 0.45 + i * 0.1, (rng() - 0.5) * 0.3), 0.6);
  // A cold glow under the skin, all over (reads it in the dark).
  glowSprite(bd, '#4fd6ff', 1.5, V(0, C, 0), 0.12);

  // Legs: two stubby wrinkled stumps with root toes.
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const lj = joint(g, V(k * 0.14, 0.3, 0), `leg${s}`);
    limb(lj.abs, V(k * 0.14, 0.34, 0), V(k * 0.17, 0.06, 0.03), 0.09, 0.075, under, 7);
    blob(lj.abs, under, k * 0.17, 0.05, 0.05, 0.09, 0.05, 0.11, 7, 4);
    for (let i = 0; i < 3; i++) {
      const a = (i - 1) * 0.6;
      M(taper([V(k * 0.17, 0.05, 0.08), V(k * 0.17 + Math.sin(a) * 0.08, 0.02, 0.08 + Math.cos(a) * 0.08), V(k * 0.17 + Math.sin(a) * 0.12, 0.0, 0.08 + Math.cos(a) * 0.12)], 0.02, 0.005, 3, 4, 1, 1), under, lj.abs);
    }
    legs[`leg${s}`] = lj.p;
  }

  return done(c, 1.0, undefined, undefined, bonesOf(c, { body: bodyJ.p, cap: capJ.p, legL: legs.legL!, legR: legs.legR! }));
};

/* ======================================================================================================== */
/* Escarcha viva (Vetas de escarcha)                                                                         */
/* ======================================================================================================== */

/**
 * Escarcha viva: an ooze of ice. A faceted mound of translucent pale-blue ice that heaves as it crawls, a dark
 * frozen heart inside with a cold light in it, a ridge of frost crystals on its back, and white rime crusting the
 * ground where it sits. Its front reaches out in a lip of ice to strike.
 *
 * Bones: `body` (the mound), `core` (the heart inside), `crest` (the crystals), `lip` (the reaching front).
 */
export const rime = (o: BaseOpts = {}): Model => {
  const c = begin(o, 0.62, 991);
  const { g, rng } = c;
  const k4 = kit(rng);
  const ice = emis('#8fc4e6', '#4f8fc8', 0.35, { transparent: true, opacity: 0.66, roughness: 0.08, metalness: 0.1, flatShading: true, bumpScale: 0 });
  const heart = std('#16263a', { roughness: 0.3, metalness: 0.3, flatShading: true });
  const crystal = emis('#eef8ff', '#9fd4ff', 0.55, { roughness: 0.15, flatShading: true });
  const frost = std('#a9c2d4', { roughness: 0.95, flatShading: true, bumpScale: 0.05 });
  const cold = emis('#ffffff', '#cfe8ff', 2.2);

  const bodyJ = joint(g, V(0, 0.02, 0), 'body');
  const geo = new THREE.IcosahedronGeometry(0.55, 2);
  // Flatten into a mound and bulge it forward and back, irregular.
  {
    const p = geo.attributes.position as THREE.BufferAttribute;
    const seen = new Map<string, number>();
    for (let i = 0; i < p.count; i++) {
      const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
      const key = `${x.toFixed(3)},${y.toFixed(3)},${z.toFixed(3)}`;
      let d = seen.get(key);
      if (d === undefined) {
        d = 1 + (rng() - 0.5) * 0.12;
        seen.set(key, d);
      }
      p.setXYZ(i, x * d * 1.05, Math.max(y, -0.32) * d * 0.72, z * d * 1.2);
    }
    geo.computeVertexNormals();
  }
  const b0 = (geo.attributes.position as THREE.BufferAttribute).array.slice();
  const body = M(geo, ice, bodyJ.abs);
  body.position.y = 0.26;

  const coreJ = joint(bodyJ.abs, V(0, 0.32, 0), 'core');
  k4.lump(coreJ.abs, heart, 0, 0.32, 0.02, 0.2, 0.16, 0.19, 0.6, 0, 0.35);
  blob(coreJ.abs, cold, 0, 0.33, 0.1, 0.05, 0.045, 0.04, 6, 4);
  glowSprite(coreJ.abs, '#cfe8ff', 0.7, V(0, 0.34, 0.14), 0.55);
  // Things it froze: a few dark slivers caught in the ice.
  for (let i = 0; i < 3; i++) {
    const m = M(new THREE.BoxGeometry(0.015, 0.16, 0.012), heart, bodyJ.abs);
    m.position.set((rng() - 0.5) * 0.5, 0.26 + rng() * 0.1, (rng() - 0.5) * 0.5);
    m.rotation.set(rng() * 3, rng() * 3, rng() * 3);
  }

  // The crest: a ridge of frost crystals down its back.
  const crestJ = joint(bodyJ.abs, V(0, 0.6, -0.1), 'crest');
  for (let i = 0; i < 9; i++) {
    const z = 0.25 - i * 0.075;
    const x = (i % 2 ? 1 : -1) * (0.04 + rng() * 0.08);
    const y = 0.26 + Math.sqrt(Math.max(0, 1 - (z / 0.66) ** 2 - (x / 0.58) ** 2)) * 0.38 - 0.04;
    k4.shard(crestJ.abs, crystal, V(x, y, z), V(x * 3, 1, -0.3 - (rng() * 0.3)), 0.22 + rng() * 0.18 - Math.abs(i - 4) * 0.02, 0.04 + rng() * 0.02);
  }
  for (const k of [-1, 1]) k4.shard(crestJ.abs, crystal, V(k * 0.3, 0.42, -0.02), V(k, 0.8, -0.2), 0.16, 0.035);

  // The lip: the front of it reaching out low.
  const lipJ = joint(bodyJ.abs, V(0, 0.18, 0.45), 'lip');
  const lip = M(new THREE.IcosahedronGeometry(0.2, 1), ice, lipJ.abs);
  lip.position.set(0, 0.14, 0.58);
  lip.scale.set(1.1, 0.55, 0.9);
  k4.shard(lipJ.abs, crystal, V(0.05, 0.2, 0.66), V(0.1, 0.6, 1), 0.12, 0.03);
  k4.shard(lipJ.abs, crystal, V(-0.08, 0.19, 0.62), V(-0.2, 0.7, 0.9), 0.1, 0.025);

  // The rime it leaves: white crust round the base.
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + rng() * 0.4;
    const r = 0.58 + rng() * 0.14;
    k4.lump(g, frost, Math.cos(a) * r * 0.95, 0.01, Math.sin(a) * r * 1.1, 0.08 + rng() * 0.05, 0.015, 0.06 + rng() * 0.04, 0.2, 0);
  }
  glowSprite(g, '#cfe8ff', 1.6, V(0, 0.3, 0), 0.12);

  // It heaves: the ice boils slowly (four baked frames).
  const t = (n: number): void => {
    const p = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) {
      const x = b0[i * 3]!, y = b0[i * 3 + 1]!, z = b0[i * 3 + 2]!;
      const k = 1 + Math.sin(x * 6 + n * 1.6) * 0.035 + Math.sin(z * 5 - n * 1.2) * 0.035;
      p.setXYZ(i, x * k, y * (2 - k), z * k);
    }
    p.needsUpdate = true;
    geo.computeVertexNormals();
  };
  return done(c, 0.85, t, undefined, bonesOf(c, { body: bodyJ.p, core: coreJ.p, crest: crestJ.p, lip: lipJ.p }));
};

/* ======================================================================================================== */
/* Eco de cristal (Geoda)                                                                                    */
/* ======================================================================================================== */

/**
 * Eco de cristal: a geode that walks. A shell of dark rock split open along its back, violet prisms bursting out
 * of the split (a few green among them), on four legs of crystal like a crab's. In the crack at its front, two
 * prisms burn like eyes. Its halves (when a bolt splits it) are the same model drawn smaller.
 *
 * Bones: `body` (the shell), `crest` (the prisms on its back), and four legs, each a `leg<i>` at the hip and a
 * `knee<i>`, turned so local +Z runs out along the leg (as the spiderling's: −X lifts, +X on the knee folds).
 * Legs 0–1 right (front, back), 2–3 left.
 */
export const shardling = (o: BaseOpts = {}): Model => {
  const c = begin(o, 0.5, 1011);
  const { g, rng } = c;
  const k4 = kit(rng);
  const rock = std('#2a2630', { roughness: 0.9, flatShading: true, bumpScale: 0.06 });
  const rockL = std('#4a4552', { roughness: 0.9, flatShading: true, bumpScale: 0.05 });
  const quartz = std('#c9c4d6', { roughness: 0.4, flatShading: true, bumpScale: 0 });
  const violet = emis('#d7a4ff', '#c56cff', 0.7, { roughness: 0.15, metalness: 0.1, flatShading: true });
  const deep = emis('#5b2a8a', '#8a3ad8', 0.45, { roughness: 0.2, flatShading: true });
  const green = emis('#b8ffd8', '#3fd88a', 0.8, { roughness: 0.15, flatShading: true });

  /** A hexagonal prism with a pointed tip, from `base` along `dir`. */
  const prism = (parent: THREE.Object3D, mat: THREE.Material, base: THREE.Vector3, dir: THREE.Vector3, len: number, r: number): void => {
    const geo = new THREE.CylinderGeometry(r, r * 1.05, len * 0.75, 6);
    geo.translate(0, len * 0.375, 0);
    const tip = new THREE.ConeGeometry(r, len * 0.25, 6);
    tip.translate(0, len * 0.875, 0);
    const m1 = along(parent, geo, mat, base, dir);
    const m2 = along(parent, tip, mat, base, dir);
    const spin = rng() * 6.3;
    m1.rotateY(spin);
    m2.rotateY(spin);
  };

  const bodyJ = joint(g, V(0, 0.42, 0), 'body');
  const bd = bodyJ.abs;
  // The shell: two halves of rock, split along the back, the inside lined with violet.
  for (const k of [-1, 1]) k4.lump(bd, rock, k * 0.16, 0.44, 0, 0.26, 0.24, 0.42, 0.2, 1, 0.22);
  k4.lump(bd, rockL, 0, 0.32, 0.02, 0.32, 0.12, 0.38, 0.2, 0);
  k4.lump(bd, deep, 0, 0.5, -0.02, 0.18, 0.18, 0.36, 0.3, 1, 0.3);
  // The pale quartz band where the shell splits, as in a geode cut open.
  for (const k of [-1, 1]) for (let i = 0; i < 4; i++) k4.lump(bd, quartz, k * 0.1, 0.62 - Math.abs(i - 1.5) * 0.03, 0.27 - i * 0.17, 0.05, 0.04, 0.1, 0.4, 0);
  // The face: a crack at the front with two prisms burning in it.
  k4.lump(bd, deep, 0, 0.47, 0.36, 0.13, 0.09, 0.06, 0.3, 0);
  for (const k of [-1, 1]) {
    prism(bd, violet, V(k * 0.05, 0.45, 0.36), V(k * 0.3, 0.2, 1), 0.08, 0.022);
    glowSprite(bd, '#e2b8ff', 0.2, V(k * 0.055, 0.47, 0.43), 0.9);
  }
  glowSprite(bd, '#c56cff', 1.3, V(0, 0.6, 0), 0.16);

  // The crest: prisms bursting out of the split.
  const crestJ = joint(bd, V(0, 0.62, 0), 'crest');
  for (let i = 0; i < 9; i++) {
    const z = 0.26 - i * 0.065;
    const x = (rng() - 0.5) * 0.12;
    const mat = i === 2 || i === 6 ? green : violet;
    prism(crestJ.abs, mat, V(x, 0.58, z), V(x * 4 + (rng() - 0.5) * 0.6, 1, (rng() - 0.5) * 0.5 - 0.2), 0.32 + rng() * 0.26 - Math.abs(i - 3.5) * 0.03, 0.035 + rng() * 0.02);
  }
  for (const k of [-1, 1]) for (let i = 0; i < 2; i++) prism(crestJ.abs, violet, V(k * 0.18, 0.62, 0.1 - i * 0.22), V(k * 1.2, 1, -0.2), 0.18, 0.03);

  // Legs: crystal segments, high knees, prism points to the ground.
  const LEG = [
    { hip: V(0.24, 0.36, 0.18), yaw: 0.75 },
    { hip: V(0.24, 0.36, -0.16), yaw: 2.3 },
  ];
  const legBones: Record<string, THREE.Object3D> = {};
  for (let i = 0; i < 4; i++) {
    const side = i < 2 ? 1 : -1;
    const L = LEG[i % 2]!;
    const hip = V(side * L.hip.x, L.hip.y, L.hip.z);
    const yaw = side * L.yaw;
    const d = V(Math.sin(yaw), 0, Math.cos(yaw));
    const knee = hip.clone().addScaledVector(d, 0.2).setY(0.54);
    const toe = hip.clone().addScaledVector(d, 0.34).setY(0.0);
    const th = yjoint(bd, hip, yaw, `leg${i}`);
    const sh = yjoint(th.abs, knee, yaw, `knee${i}`);
    legBones[`leg${i}`] = th.p;
    legBones[`knee${i}`] = sh.p;
    k4.lump(th.abs, rock, hip.x, hip.y, hip.z, 0.07, 0.07, 0.07, 0.5, 0);
    prism(th.abs, deep, hip, knee.clone().sub(hip), hip.distanceTo(knee) + 0.03, 0.05);
    k4.lump(sh.abs, rock, knee.x, knee.y, knee.z, 0.06, 0.05, 0.06, 0.5, 0);
    prism(sh.abs, violet, knee, toe.clone().sub(knee), knee.distanceTo(toe) + 0.02, 0.045);
  }

  return done(c, 1.0, undefined, undefined, bonesOf(c, { body: bodyJ.p, crest: crestJ.p, ...legBones }));
};

/* ======================================================================================================== */
/* Escribiente (Archivo ahogado)                                                                             */
/* ======================================================================================================== */

/**
 * Escribiente: a scribe of the drowned archive, gaunt and too tall, bent over as if still at its desk. A cold grey
 * robe soaked to black at the hem, a deep hood with nothing in it but two pale points, long grey hands. In the
 * right a black quill whose nib burns sealing-wax red (what it writes on the floor); from the left unrolls a scroll
 * down to the ground, written over in ink, red wax seals hanging off it. Ink drips from its sleeves and hem.
 *
 * Bones: body (hips: the robe glides), chest, head, arms in two (arm, fore), `scroll` (the hanging scroll, from the
 * left hand). Conventions as human.ts.
 */
export const scribe = (o: BaseOpts = {}): Model => {
  const c = begin(o, 0.5, 1031);
  const { g, rng } = c;
  const k4 = kit(rng);
  const robe = cloth('#363d46', { side: THREE.DoubleSide });
  const robeD = cloth('#16191e', { side: THREE.DoubleSide });
  const ink = std('#050608', { roughness: 0.08, metalness: 0.5, bumpScale: 0 });
  const hand = std('#7d868e', { roughness: 0.7 });
  const paper = std('#7a838c', { roughness: 0.9, side: THREE.DoubleSide, bumpScale: 0.03 });
  const wax = std('#9a1f26', { roughness: 0.4 });
  const nib = emis('#ffd2c8', '#ff4f3a', 2.2);
  const eye = emis('#ffffff', '#cfdbe6', 2.0);

  /** A ragged robe skirt: an open cone, its hem cut ragged, `wet` darkening it near the bottom. */
  const skirt = (parent: THREE.Object3D, y0: number, y1: number, r0: number, r1: number, mat: THREE.Material, z = 0): THREE.Mesh => {
    const geo = new THREE.CylinderGeometry(r0, r1, y1 - y0, 12, 3, true);
    const p = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) {
      const y = p.getY(i);
      if (y < -(y1 - y0) / 2 + 0.001) p.setY(i, y + (rng() * 0.08) * ((i % 2) + 0.4));
      // Folds: the cloth ripples round.
      const a = Math.atan2(p.getZ(i), p.getX(i));
      const f = 1 + 0.08 * Math.sin(a * 6) * (0.5 - y / (y1 - y0));
      p.setX(i, p.getX(i) * f);
      p.setZ(i, p.getZ(i) * f);
    }
    geo.computeVertexNormals();
    const m = M(geo, mat, parent);
    m.position.set(0, (y0 + y1) / 2, z);
    return m;
  };

  // The robe from the hips down: grey, the hem soaked black.
  const bodyB = joint(g, V(0, 0.95, 0), 'body');
  const bd = bodyB.abs;
  skirt(bd, 0.22, 1.05, 0.17, 0.36, robe);
  skirt(bd, 0.0, 0.3, 0.33, 0.4, robeD);
  // Tatters hanging off the hem, and ink running down the cloth.
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.6;
    const t = k4.panel(bd, V(Math.sin(a) * 0.33, 0.34, Math.cos(a) * 0.33), 0.1, 0.3, 0.01, 1, robeD, 0.7);
    t.rotation.y = a;
  }
  for (let i = 0; i < 4; i++) {
    const a = -0.9 + i * 0.6;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 4; j++) {
      const y = 1.0 - j * 0.24;
      const r = 0.18 + (1.0 - y) * 0.2 + 0.012;
      pts.push(V(Math.sin(a + j * 0.03) * r, y, Math.cos(a + j * 0.03) * r));
    }
    k4.crack(bd, pts, 0.011, ink, 0.01);
  }
  // Ink running off the hem, and its pool.
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.3;
    const d = blob(bd, ink, Math.cos(a) * 0.37, 0.08 + rng() * 0.06, Math.sin(a) * 0.37, 0.018, 0.06, 0.018, 5, 4);
    d.scale.y = 0.05 + rng() * 0.05;
  }
  const pool = M(new THREE.CircleGeometry(0.5, 12), ink, g);
  pool.rotation.x = -Math.PI / 2;
  pool.position.y = 0.006;
  pool.scale.set(1, 0.85, 1);

  // Chest: gaunt, bent forward over the desk that isn't there.
  const chestB = joint(bd, V(0, 1.05, 0), 'chest');
  chestB.p.rotation.x = 0.22;
  const ch = chestB.abs;
  skirt(ch, 1.0, 1.55, 0.13, 0.2, robe, 0.02);
  blob(ch, robe, 0, 1.5, 0.0, 0.2, 0.12, 0.15, 8, 5);
  // A ragged mantle over the shoulders, darker.
  skirt(ch, 1.25, 1.6, 0.12, 0.3, robeD, 0.02);
  // A stole of darker cloth down the front, a wax seal on it.
  const stole = k4.panel(ch, V(0, 1.55, 0.13), 0.12, 0.6, 0.02, 1, robeD, 0.3);
  stole.rotation.x = -0.12;
  blob(ch, wax, 0.0, 1.3, 0.19, 0.035, 0.035, 0.012, 7, 4);
  // A belt of cord with a little book hanging.
  M(new THREE.TorusGeometry(0.18, 0.015, 4, 12), robeD, ch).position.set(0, 1.06, 0.02);
  (ch.children[ch.children.length - 1] as THREE.Mesh).rotation.x = Math.PI / 2;
  const book = M(new THREE.BoxGeometry(0.09, 0.12, 0.04), std('#2d1d22', { roughness: 0.7 }), ch);
  book.position.set(-0.15, 0.94, 0.12);
  book.rotation.z = 0.2;

  // Head: the deep hood, a pointed tail hanging back, nothing inside but two pale points.
  const headB = joint(ch, V(0, 1.6, 0.06), 'head');
  const hd = headB.abs;
  blob(hd, robe, 0, 1.73, 0.06, 0.15, 0.17, 0.17, 9, 7);
  M(taper([V(0, 1.84, 0.0), V(0, 1.86, -0.12), V(0, 1.74, -0.26)], 0.09, 0.01, 4, 6, 1, 1), robe, hd);
  // The opening: a deep cowl reaching forward, a black void inside it.
  const cowl = along(hd, new THREE.CylinderGeometry(0.105, 0.125, 0.12, 9, 1, true), robe, V(0, 1.71, 0.17), V(0, -0.15, 1));
  cowl.scale.set(1, 1, 1.15);
  blob(hd, ink, 0, 1.7, 0.16, 0.095, 0.11, 0.06, 8, 5);
  for (const k of [-1, 1]) {
    blob(hd, eye, k * 0.04, 1.71, 0.2, 0.012, 0.008, 0.008, 5, 3);
    glowSprite(hd, '#d6e4f0', 0.08, V(k * 0.04, 1.71, 0.215), 0.75);
  }

  // Arms: long sleeves, grey hands with long fingers.
  const arms: Record<string, THREE.Object3D> = {};
  let foreL: THREE.Object3D = ch;
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const armB = joint(ch, V(k * 0.19, 1.5, 0.0), `arm${s}`);
    const elbow = V(k * 0.27, 1.2, 0.12);
    M(taper([V(k * 0.19, 1.52, 0), V(k * 0.25, 1.36, 0.06), elbow], 0.07, 0.065, 4, 7, 1, 0.3), robe, armB.abs);
    const foreB = joint(armB.abs, elbow, `fore${s}`);
    const fo = foreB.abs;
    const wrist = V(k * 0.22, 1.12, 0.42);
    // The sleeve widens to a hanging cuff, ink dripping from it.
    along(fo, new THREE.CylinderGeometry(0.11, 0.065, 0.3, 8, 1, true), robe, elbow.clone().lerp(wrist, 0.55), wrist.clone().sub(elbow));
    for (let i = 0; i < 2; i++) blob(fo, ink, k * 0.22 + (i - 0.5) * 0.06, 1.0 - i * 0.03, 0.35, 0.014, 0.05, 0.014, 5, 3);
    const palm = V(k * 0.22, 1.1, 0.5);
    blob(fo, hand, palm.x, palm.y, palm.z, 0.04, 0.025, 0.05, 6, 4);
    for (let f = 0; f < 3; f++) {
      const sp = (f - 1) * 0.022;
      M(taper([palm, V(palm.x + sp, palm.y - 0.04, palm.z + 0.07), V(palm.x + sp * 1.3, palm.y - 0.1, palm.z + 0.09)], 0.011, 0.004, 3, 4, 1, 1), hand, fo);
    }
    if (k > 0) {
      // The quill: a long black feather, the nib burning red.
      const tip = V(0.22, 0.98, 0.62);
      const top = V(0.34, 1.45, 0.38);
      limb(fo, tip, top, 0.006, 0.004, ink, 4);
      const vane = M(taper([tip.clone().lerp(top, 0.25), tip.clone().lerp(top, 0.6).add(V(0.03, 0, 0)), top.clone().add(V(0.04, 0.06, -0.03))], 0.035, 0.008, 5, 4, 0.25, 1), std('#15171b', { roughness: 0.6 }), fo);
      vane.name = 'vane';
      blob(fo, nib, tip.x, tip.y, tip.z, 0.012, 0.012, 0.012, 5, 3);
      glowSprite(fo, '#ff4f3a', 0.3, tip, 0.85);
    }
    arms[`arm${s}`] = armB.p;
    arms[`fore${s}`] = foreB.p;
    if (k < 0) foreL = fo;
  }

  // The scroll: unrolling from the left hand down to the floor, written over, wax seals hanging off it.
  const scrollJ = joint(foreL, V(-0.22, 1.1, 0.5), 'scroll');
  const sc = scrollJ.abs;
  {
    const W = 0.2, segs = 6;
    const geo = new THREE.PlaneGeometry(W, 1.0, 1, segs);
    const p = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) {
      const v = (0.5 - p.getY(i)); // 0 at the hand, 1 at the floor
      const y = 1.08 - v * 1.02;
      const z = 0.5 + Math.sin(v * 2.4) * 0.12 + v * 0.1;
      p.setXYZ(i, -0.22 + p.getX(i), Math.max(0.14, y), z + (v > 0.95 ? v * 0.2 : 0));
    }
    geo.computeVertexNormals();
    M(geo, paper, sc);
    // The roll at the hand.
    const roll = M(new THREE.CylinderGeometry(0.03, 0.03, W + 0.04, 7), paper, sc);
    roll.rotation.z = Math.PI / 2;
    roll.position.set(-0.22, 1.1, 0.5);
    // Lines of writing: thin black strokes on its front.
    for (let i = 0; i < 6; i++) {
      const v = 0.1 + i * 0.13;
      const y = 1.08 - v * 1.02;
      const z = 0.5 + Math.sin(v * 2.4) * 0.12 + v * 0.1 + 0.006;
      const ln = M(new THREE.BoxGeometry(0.11 + rng() * 0.05, 0.012, 0.003), ink, sc);
      ln.position.set(-0.22 + (rng() - 0.5) * 0.03, y, z);
      ln.rotation.x = -Math.cos(v * 2.4) * 0.3;
    }
    // Wax seals on ribbons.
    for (const [x, v] of [[-0.1, 0.35], [-0.32, 0.6]] as const) {
      const y = 1.08 - v * 1.02;
      const z = 0.5 + Math.sin(v * 2.4) * 0.12 + v * 0.1;
      limb(sc, V(x, y, z + 0.01), V(x + 0.01, y - 0.12, z + 0.03), 0.006, 0.006, wax, 3);
      const s = blob(sc, wax, x + 0.01, y - 0.14, z + 0.03, 0.03, 0.03, 0.01, 7, 3);
      s.rotation.y = 0.2;
    }
  }

  return done(c, 1.95, undefined, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p,
    armL: arms.armL!, foreL: arms.foreL!, armR: arms.armR!, foreR: arms.foreR!, scroll: scrollJ.p,
  }));
};

/* ======================================================================================================== */
/* Fundido (Forja muerta)                                                                                    */
/* ======================================================================================================== */

/**
 * Fundido: a man-shaped pour of metal that never cooled. A red-hot body under plates of dark crust that crack as
 * it moves, the heat showing in every seam and joint; a head with no face but a slit that burns white-hot; heavy
 * hands, the right one dripping. Where it stands the metal runs off it.
 *
 * Bones: body (hips), chest, head, arms in two (arm, fore), legs in two (leg, shin). Conventions as human.ts.
 */
export const molten = (o: BaseOpts = {}): Model => {
  const c = begin(o, 0.6, 1051);
  const { g, rng } = c;
  const k4 = kit(rng);
  const hot = emis('#d8481e', '#ff3a10', 1.0);
  const glow = emis('#5a140a', '#ff2a0a', 0.55, { roughness: 0.6 });
  const crust = std('#1c1513', { roughness: 0.95, flatShading: true, bumpScale: 0.06 });
  const crustL = std('#3a2a26', { roughness: 0.9, flatShading: true, bumpScale: 0.05 });
  const white = emis('#ffffff', '#ffd2a0', 2.6);

  /** A limb: its hot core, crust plates over most of it (the heat shows between). */
  const limbOf = (parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, r: number, plates = 2): void => {
    limb(parent, a, b, r * 0.8, r * 0.7, hot, 7);
    // Plates of crust, each off to one side of the limb, so the heat shows down the seams between them.
    for (let i = 0; i < plates * 2; i++) {
      const p = a.clone().lerp(b, (Math.floor(i / 2) + 0.5) / plates);
      const ang = (i % 2) * Math.PI + rng() * 1.2;
      const m = k4.lump(parent, i % 3 ? crust : crustL, p.x + Math.cos(ang) * r * 0.45, p.y, p.z + Math.sin(ang) * r * 0.45, r * 0.75, a.distanceTo(b) / plates * 0.6, r * 0.75, 0.6, 0, 0.35);
      m.lookAt(m.position.clone().add(b.clone().sub(a)));
    }
  };

  // Legs.
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = joint(g, V(k * 0.17, 0.95, 0), `leg${s}`);
    limbOf(legB.abs, V(k * 0.17, 0.98, 0), V(k * 0.2, 0.52, 0.03), 0.11);
    const shinB = joint(legB.abs, V(k * 0.2, 0.52, 0.03), `shin${s}`);
    blob(shinB.abs, glow, k * 0.2, 0.52, 0.04, 0.09, 0.08, 0.09, 7, 5);
    limbOf(shinB.abs, V(k * 0.2, 0.5, 0.03), V(k * 0.21, 0.1, 0.0), 0.1);
    k4.lump(shinB.abs, crust, k * 0.21, 0.06, 0.06, 0.12, 0.07, 0.17, 0.3, 0);
    blob(shinB.abs, hot, k * 0.21, 0.02, 0.1, 0.1, 0.03, 0.12, 7, 3);
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  // Hips and chest.
  const bodyB = joint(g, V(0, 1.0, 0), 'body');
  blob(bodyB.abs, hot, 0, 1.02, 0, 0.22, 0.14, 0.15, 8, 5);
  k4.lump(bodyB.abs, crust, 0, 1.0, 0.04, 0.26, 0.12, 0.18, 0.3, 0);
  const chestB = joint(bodyB.abs, V(0, 1.1, 0), 'chest');
  const ch = chestB.abs;
  blob(ch, hot, 0, 1.42, 0.02, 0.3, 0.32, 0.22, 9, 6);
  // Crust plates over the chest, back and shoulders, leaving glowing seams.
  k4.lump(ch, crust, -0.14, 1.52, 0.1, 0.17, 0.2, 0.15, 0.4, 0, 0.25);
  k4.lump(ch, crust, 0.15, 1.5, 0.11, 0.17, 0.21, 0.15, 0.4, 0, 0.25);
  k4.lump(ch, crustL, 0.02, 1.22, 0.1, 0.2, 0.12, 0.13, 0.4, 0, 0.25);
  k4.lump(ch, crust, 0, 1.45, -0.1, 0.3, 0.32, 0.16, 0.3, 1, 0.2);
  for (const k of [-1, 1]) k4.lump(ch, crustL, k * 0.3, 1.68, 0, 0.15, 0.1, 0.15, 0.4, 0);
  // The furnace in its chest: a white-hot split between the plates.
  k4.crack(ch, [V(0, 1.7, 0.2), V(0.01, 1.55, 0.25), V(-0.01, 1.4, 0.25), V(0.02, 1.25, 0.21)], 0.022, white, 0.03);
  for (let i = 0; i < 3; i++) {
    const a = -0.6 + i * 0.6;
    k4.crack(ch, [V(0, 1.45 + i * 0.08, 0.25), V(Math.sin(a) * 0.16, 1.48 + i * 0.1, 0.2), V(Math.sin(a) * 0.27, 1.5 + i * 0.12, 0.08)], 0.01, hot, 0.03);
  }
  glowSprite(ch, '#ff6a3a', 0.9, V(0, 1.48, 0.3), 0.55);

  // Head: a featureless lump of crust, a white-hot slit across it, a short neck of glowing metal.
  const headB = joint(ch, V(0, 1.74, 0.04), 'head');
  const hd = headB.abs;
  limb(hd, V(0, 1.7, 0.03), V(0, 1.84, 0.06), 0.08, 0.075, glow, 6);
  k4.lump(hd, crust, 0, 1.92, 0.07, 0.14, 0.15, 0.14, 0.3, 1, 0.18);
  const slit = M(new THREE.BoxGeometry(0.16, 0.022, 0.05), white, hd);
  slit.position.set(0, 1.92, 0.18);
  glowSprite(hd, '#ffb070', 0.42, V(0, 1.92, 0.22), 0.85);
  // Drips of metal running from the slit.
  blob(hd, hot, 0.04, 1.86, 0.19, 0.012, 0.04, 0.012, 5, 3);

  // Arms: heavy, the hands great lumps; the right one drips.
  const arms: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const armB = joint(ch, V(k * 0.33, 1.62, 0.0), `arm${s}`);
    blob(armB.abs, glow, k * 0.33, 1.62, 0, 0.11, 0.11, 0.11, 7, 5);
    const elbow = V(k * 0.43, 1.22, 0.04);
    limbOf(armB.abs, V(k * 0.35, 1.6, 0), elbow, 0.1);
    const foreB = joint(armB.abs, elbow, `fore${s}`);
    const fo = foreB.abs;
    const wrist = V(k * 0.45, 0.86, 0.12);
    limbOf(fo, elbow, wrist, 0.09);
    blob(fo, glow, elbow.x, elbow.y, elbow.z, 0.085, 0.085, 0.085, 6, 4);
    // The hand: a great clot of metal, crusted on the back, glowing fingers.
    blob(fo, hot, k * 0.46, 0.76, 0.14, 0.1, 0.12, 0.1, 7, 5);
    k4.lump(fo, crust, k * 0.49, 0.8, 0.1, 0.11, 0.1, 0.1, 0.4, 0);
    for (let f = 0; f < 3; f++) {
      const sp = (f - 1) * 0.045;
      M(taper([V(k * 0.46 + sp, 0.7, 0.18), V(k * 0.46 + sp * 1.2, 0.62, 0.22), V(k * 0.46 + sp * 1.3, 0.56, 0.2)], 0.024, 0.01, 3, 5, 1, 1), hot, fo);
    }
    if (k > 0) {
      for (let i = 0; i < 3; i++) {
        const d = blob(fo, hot, 0.44 + (i - 1) * 0.04, 0.5 - i * 0.06, 0.18, 0.016, 0.04 + i * 0.01, 0.016, 5, 3);
        d.name = 'drip';
      }
      glowSprite(fo, '#ff7a3a', 0.5, V(0.46, 0.68, 0.16), 0.6);
    }
    arms[`arm${s}`] = armB.p;
    arms[`fore${s}`] = foreB.p;
  }

  // What runs off it: a pool of glowing metal and the heat round it.
  const pool = M(new THREE.CircleGeometry(0.42, 12), glow, g);
  pool.rotation.x = -Math.PI / 2;
  pool.position.set(0.05, 0.006, 0.05);
  pool.scale.set(1, 0.8, 1);
  for (let i = 0; i < 4; i++) {
    const a = rng() * 6.3;
    blob(g, hot, Math.cos(a) * 0.3, 0.008, Math.sin(a) * 0.26, 0.07, 0.008, 0.05, 6, 2);
  }
  glowSprite(g, '#ff5a3a', 2.0, V(0, 0.9, 0), 0.14);

  return done(c, 2.0, undefined, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p,
    armL: arms.armL!, foreL: arms.foreL!, armR: arms.armR!, foreR: arms.foreR!,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!,
  }));
};

